import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Heart, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestedAmounts = [10, 25, 50, 100, 250, 500];

const DonatePage = () => {
  const [loading, setLoading] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [form, setForm] = useState({
    donor_name: '',
    donor_email: '',
    message: ''
  });

  const amount = selectedAmount || parseFloat(customAmount) || 0;

  const handleDonate = async (e) => {
    e.preventDefault();
    
    if (amount < 1) {
      toast.error('Minimum donation is $1');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/donations/checkout`, {
        amount,
        donor_name: form.donor_name || null,
        donor_email: form.donor_email || null,
        message: form.message || null,
        origin_url: window.location.origin
      });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process donation');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="donate-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-saffron-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-10 h-10 text-saffron-600" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Support Khalsa Sidak
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Your seva (selfless service) helps us maintain and grow this platform for the global Sikh community.
          </p>
        </div>

        {/* Donation Card */}
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">Make a Donation</CardTitle>
            <CardDescription>Choose an amount or enter a custom amount</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDonate} className="space-y-6">
              {/* Suggested Amounts */}
              <div className="grid grid-cols-3 gap-3">
                {suggestedAmounts.map((amt) => (
                  <Button
                    key={amt}
                    type="button"
                    variant={selectedAmount === amt ? 'default' : 'outline'}
                    className={`text-lg py-6 ${
                      selectedAmount === amt 
                        ? 'bg-saffron-500 hover:bg-saffron-600 text-white' 
                        : 'border-saffron-200 hover:bg-saffron-50'
                    }`}
                    onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                    data-testid={`amount-${amt}`}
                  >
                    ${amt}
                  </Button>
                ))}
              </div>

              {/* Custom Amount */}
              <div>
                <Label htmlFor="custom">Custom Amount</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <Input
                    id="custom"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Enter amount"
                    className="pl-8 text-lg py-6"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                    data-testid="custom-amount"
                  />
                </div>
              </div>

              {/* Optional Info */}
              <div className="space-y-4 pt-4 border-t">
                <p className="text-sm text-gray-500">Optional: Leave your name and message</p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      id="name"
                      value={form.donor_name}
                      onChange={(e) => setForm({ ...form, donor_name: e.target.value })}
                      placeholder="Anonymous"
                      data-testid="donor-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email (for receipt)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.donor_email}
                      onChange={(e) => setForm({ ...form, donor_email: e.target.value })}
                      placeholder="your@email.com"
                      data-testid="donor-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Leave a message or dedication..."
                    rows={3}
                    data-testid="donor-message"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full btn-primary text-lg py-6"
                disabled={amount < 1 || loading}
                data-testid="donate-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Heart className="w-5 h-5 mr-2" />
                    Donate ${amount || '0'}
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-gray-500">
                Secure payment powered by Stripe. You can also donate via Zelle or PayPal to Khalsasidak13@gmail.com
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Why Donate */}
        <div className="mt-12 text-center">
          <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">
            Your Donation Helps Us
          </h3>
          <div className="grid sm:grid-cols-3 gap-6 text-sm text-gray-600">
            <div>
              <div className="w-12 h-12 bg-royal-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-xl">🌐</span>
              </div>
              <p>Maintain the platform and servers</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-royal-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-xl">📚</span>
              </div>
              <p>Add more Sikh educational content</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-royal-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-xl">🤝</span>
              </div>
              <p>Support community programs</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;
